import { useEffect, useState } from "react";
import { FaVideo, FaImage } from "react-icons/fa";
import {
  CardWrapper,
  VideoUploadSection,
  ImageUploadSection,
  InputWithSelect,
  InputWithUnit,
  SubmitButton,
  PageHeader,
  InputField,
  SmallInputField,
  RadioField,
  LocationSelector,
  CustomDropdown,
} from "../../../../components/admin/common/form-elements";
import {
  AREA_UNITS,
  INITIAL_AREA,
  INITIAL_LOCATION,
} from "../../../../utils/constants";
import { showError, showSuccess } from "../../../../utils/toastUtils";
import api from "../../../../configs/api";
import { useNavigate, useParams } from "react-router-dom";
import AddCommercialPropertyPageSkeleton from "../../../../components/admin/skeleton-loaders/properties/AddCommercialPropertyPageSkeleton";
import CompanyModal from "../../../../components/modals/CompanyModal";
import { FiBriefcase, FiEdit, FiPlus } from "react-icons/fi";
import { useAuth } from "../../../../context/AuthContext";

const AddCommercialPropertyPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const initialFormData = {
    propertyName: "",
    videoUrl: "",
    images: [],
    suitableFor: "",
    propertyAge: "",
    expectedPrice: "",
    pricePerSqft: "",
    carpetArea: INITIAL_AREA,
    superArea: INITIAL_AREA,
    floor: "",
    availability: "",
    transactionType: "",
    location: INITIAL_LOCATION,
    companyId: "",
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [companyModalMode, setCompanyModalMode] = useState("create");

  const { user } = useAuth();
  const showCompanyDropdown = user?.role === "agent-builder";

  // useEffect(() => {
  //   console.log("formData", formData);
  // }, [formData]);

  useEffect(() => {
    if (isEditMode && id) {
      const fetchProperty = async () => {
        setIsLoading(true);
        try {
          const resp = await api.get(`/properties/${id}`);
          const data = resp?.data?.data;
          // console.log("fetching ropertty :edit mode", data);
          setFormData({
            ...initialFormData,
            propertyName: data.title || "",
            videoUrl: data.videos?.[0] || "",
            images: data.images || [],
            suitableFor: data.property?.suitableFor || "",
            companyId: data.property?.companyId || null,
            propertyAge: data.property?.propertyAge || "",
            expectedPrice:
              data.property?.allowedForConstructionDetails?.expectedPrice || "",
            pricePerSqft:
              data.property?.allowedForConstructionDetails?.pricePerSqft || "",
            carpetArea:
              data.property?.allowedForConstructionDetails?.carpetArea ||
              INITIAL_AREA,
            superArea:
              data.property?.allowedForConstructionDetails?.superArea ||
              INITIAL_AREA,
            floor: data.property?.allowedForConstructionDetails?.floor || "",
            availability:
              data.property?.allowedForConstructionDetails?.availability || "",
            transactionType: data.property?.transactionType || "",
            location: data.location || INITIAL_LOCATION,
          });
        } catch (err) {
          showError("Failed to load property details");
        } finally {
          setIsLoading(false);
        }
      };
      fetchProperty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const fetchCompanies = async () => {
    try {
      const res = await api.get(`/companies`);
      // console.log("companies:", res.data.companies);
      setCompanies(res.data?.companies);
    } catch (error) {
      console.error("Failed to fetch companies", error);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAreaChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
  };

  const validateForm = () => {
    const missing = [];

    if (!formData.propertyName?.trim()) missing.push("Property Name");
    if (!formData.location?.coordinates?.length) missing.push("Location");

    if (!formData.images || formData.images.length < 4)
      missing.push("At least 5 Property Images");

    if (!formData.availability) missing.push("Availability Status");
    if (!formData.suitableFor) missing.push("Suitable For");
    if (!formData.propertyAge) missing.push("Property Age");
    if (!formData.expectedPrice) missing.push("Expected Price");
    if (!formData.carpetArea?.value) missing.push("Carpet Area");

    if (missing.length > 0) {
      const firstMissing = missing[0];
      showError(`Please fill out: ${firstMissing}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      title: formData?.propertyName,
      type: "commercial",

      images: formData?.images,
      videos: [formData?.videoUrl],

      location: formData.location,
      property: {
        propertyAge: formData?.propertyAge,
        suitableFor: formData?.suitableFor,
        allowedForConstructionDetails: {
          expectedPrice: formData?.expectedPrice,
          pricePerSqft: formData?.pricePerSqft,
          carpetArea: formData?.carpetArea,
          superArea: formData?.superArea,
          floor: formData?.floor,
          availability: formData?.availability,
        },
        transactionType: formData?.transactionType,
        companyId: formData?.companyId,
      },
    };

    setIsSubmitting(true);

    // console.log("payload:", payload);

    try {
      if (isEditMode) {
        await api.put(`/properties/${id}`, payload);
        showSuccess("Property Updated Successfully");
      } else {
        await api.post(`/properties`, payload);
        showSuccess("Property Created Successfully");
      }
      setFormData(initialFormData);
      navigate(-1);
    } catch (error) {
      showError(error?.response?.data?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalSuccess = (company) => {
    setSelectedCompany(company);
    fetchCompanies();
    setFormData((prev) => ({ ...prev, companyId: company?._id }));
  };

  if (isLoading && isEditMode) return <AddCommercialPropertyPageSkeleton />;

  return (
    <div className="min-h-screen  bg-background font-poppins">
      {/* Header */}
      <PageHeader
        title={
          isEditMode ? "Edit Commercial Property" : "Add Commercial Property"
        }
      />

      {/* Main Content */}
      <div className="max-full mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
          {/* Left Column */}
          <div className="space-y-6">
            <CardWrapper>
              <InputField
                label="Property name"
                name="propertyName"
                value={formData.propertyName}
                onChange={handleInputChange}
                placeholder="E.g. Covenant Commercial Complex"
              />
            </CardWrapper>

            <CardWrapper
              title="About your project"
              subtitle="A video is worth a thousand pictures. Properties with videos get higher page views"
            >
              <VideoUploadSection
                icon={FaVideo}
                prefillData={formData.videoUrl ? [formData.videoUrl] : []}
                onFileSelect={(videoUrl) => {
                  // console.log("videoUrl", videoUrl);
                  setFormData((prev) => ({ ...prev, videoUrl }));
                }}
                buttonText="+ Upload Video"
                description="Upload video of min size 20 MB in format  .mp4 video duration should be less than 10 min`s."
              />
            </CardWrapper>

            {/* Add Photos */}
            <CardWrapper
              title="Add photos of your project"
              subtitle="A picture is worth a thousand words. 80% of buyers look at photos before buying."
            >
              {}
              <ImageUploadSection
                icon={FaImage}
                prefillData={formData?.images}
                onChange={(uploadedImages) => {
                  // console.log("uploadedImages", uploadedImages);
                  setFormData((prev) => ({ ...prev, images: uploadedImages }));
                }}
                buttonText="↓ Add atleast 5 photos"
                description="Minimum 5 photos | Max size 10MB | Formats: png, jpg, gif, webp, heic, heif"
                badge="5"
              />
            </CardWrapper>

            {/* Suitable For */}
            <CardWrapper>
              <InputField
                label="Suitable For"
                name="suitableFor"
                value={formData.suitableFor}
                onChange={handleInputChange}
                placeholder="e.g- Retail , Shop , Office"
              />
            </CardWrapper>

            {/* Property Age */}
            <CardWrapper>
              <InputField
                type="number"
                label="Property Age (years)"
                name="propertyAge"
                value={formData.propertyAge}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    propertyAge: Number(e.target.value),
                  }));
                }}
                placeholder="E.g- 2 years old"
              />
            </CardWrapper>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {showCompanyDropdown && (
              <CardWrapper title="Select Company">
                <CustomDropdown
                  value={formData.companyId}
                  onChange={(val) => {
                    const selected = companies.find((c) => c._id === val);
                    setSelectedCompany(selected || null);
                    setFormData((prev) => ({
                      ...prev,
                      companyId: val,
                    }));
                  }}
                  options={companies.map((c) => ({
                    value: c._id,
                    logo: c.companyLogo,
                    label: c.companyName,
                  }))}
                  placeholder="Choose existing company..."
                  className={
                    "border-gray-300 bg-white focus:ring-1 focus:ring-primary focus:outline-none"
                  }
                />

                {/* Selected company preview (read-only) */}
                {selectedCompany && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                        {selectedCompany.companyLogo ? (
                          <img
                            src={selectedCompany.companyLogo}
                            className="w-full h-full object-cover"
                            alt="Company logo"
                          />
                        ) : (
                          <FiBriefcase className="text-gray-400 text-xl" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-text-primary mb-1">
                          {selectedCompany.companyName}
                        </p>
                        {selectedCompany.reraId && (
                          <p className="text-xs text-text-secondary">
                            RERA: {selectedCompany.reraId}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="my-3 text-sm font-medium text-center">OR</div>

                {/* Open modal: create or edit */}
                <button
                  type="button"
                  onClick={() => {
                    if (selectedCompany) {
                      setCompanyModalMode("edit");
                    } else {
                      setCompanyModalMode("create");
                    }
                    setIsCompanyModalOpen(true);
                  }}
                  className="text-sm font-medium text-primary hover:text-text-primary cursor-pointer flex items-center gap-1"
                >
                  <FiEdit className="w-4 h-4" />
                  {selectedCompany
                    ? "Edit Selected Company"
                    : "Create New Company"}
                </button>
                {selectedCompany && (
                  <button
                    type="button"
                    onClick={() => {
                      setCompanyModalMode("create");
                      setIsCompanyModalOpen(true);
                    }}
                    className="mt-2 text-sm font-medium text-primary hover:text-text-primary cursor-pointer flex items-center gap-1"
                  >
                    <FiPlus className="w-4 h-4" />
                    Create New Company
                  </button>
                )}

                {/* Modal itself */}
                <CompanyModal
                  open={isCompanyModalOpen}
                  mode={companyModalMode}
                  initialData={
                    companyModalMode === "edit" ? selectedCompany : null
                  }
                  onClose={() => setIsCompanyModalOpen(false)}
                  onSuccess={handleModalSuccess}
                />
              </CardWrapper>
            )}
            <CardWrapper title="Floor Allowed For Construction">
              {/* Carpet Area */}
              <div className="mb-4">
                <InputWithUnit
                  type="number"
                  label="Carpet Area"
                  name="carpetArea"
                  value={formData?.carpetArea?.value}
                  selectedUnit={formData?.carpetArea?.unit}
                  onChange={(e) =>
                    handleAreaChange(
                      "carpetArea",
                      "value",
                      Number(e.target.value)
                    )
                  }
                  onUnitChange={(unit) =>
                    handleAreaChange("carpetArea", "unit", unit)
                  }
                  placeholder="Enter area"
                  units={AREA_UNITS}
                />
              </div>

              {/* Super Area */}
              <div className="mb-4">
                <InputWithUnit
                  type="number"
                  label="Super Area"
                  name="superArea"
                  value={formData?.superArea?.value}
                  selectedUnit={formData?.superArea?.unit}
                  onChange={(e) =>
                    handleAreaChange(
                      "superArea",
                      "value",
                      Number(e.target.value)
                    )
                  }
                  onUnitChange={(unit) =>
                    handleAreaChange("superArea", "unit", unit)
                  }
                  placeholder="Enter area"
                  units={AREA_UNITS}
                />
              </div>

              {/* Price Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <SmallInputField
                  label={"Expected Price"}
                  type="number"
                  name="expectedPrice"
                  value={formData.expectedPrice}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      expectedPrice: Number(e.target.value),
                    }));
                  }}
                  placeholder="₹ Expected Price"
                />
                <SmallInputField
                  label={"Price per sqft"}
                  type="number"
                  name="pricePerSqft"
                  value={formData.pricePerSqft}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      pricePerSqft: Number(e.target.value),
                    }));
                  }}
                  placeholder="₹ Price per sqft."
                />
              </div>

              {/* Floor and Availability */}
              <SmallInputField
                type="number"
                label="Floor"
                name="floor"
                value={formData.floor}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    floor: Number(e.target.value),
                  }));
                }}
                placeholder="0 for Ground , 1 for first & so on"
              />
            </CardWrapper>

            <CardWrapper title={"Availability"}>
              <RadioField
                name="availability"
                value={formData?.availability}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, availability: value }));
                }}
                options={[
                  { label: "Ready to move", value: "ready_to_move" },
                  {
                    label: "Under Construction",
                    value: "under_construction",
                  },
                ]}
              />
            </CardWrapper>
            {/* Transaction Type */}
            <CardWrapper title={"Transaction Type"}>
              <InputWithSelect
                name="transactionType"
                placeholder="Transaction Type"
                selectName="transactionType"
                selectValue={formData?.transactionType}
                selectOptions={[
                  { label: "Resale", value: "resale" },
                  { label: "Sale", value: "sale" },
                  { label: "Lease", value: "lease" },
                  { label: "Rent", value: "rent" },
                ]}
                onSelectChange={(value) => {
                  setFormData({
                    ...formData,
                    transactionType: value,
                  });
                }}
                disabled={false}
              />
            </CardWrapper>
            {/* Fetch Location */}
            <CardWrapper title="Property Location">
              <LocationSelector
                value={formData.location}
                onChange={(updatedLocation) =>
                  setFormData((prev) => ({
                    ...prev,
                    location: updatedLocation,
                  }))
                }
              />
            </CardWrapper>

            {/* Submit Button */}
            <SubmitButton
              isLoading={isSubmitting}
              onClick={handleSubmit}
              label={isEditMode ? "Save Changes" : "Add Property"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCommercialPropertyPage;
